import React from 'react';
import Animation from '../Animation';
import useTranslations from '../../hooks/useTranslations';
import styled from 'styled-components/macro';
import AttachmentIcon from '@material-ui/icons/Attachment';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { LinearProgress as MuiLinearProgress } from '@material-ui/core';
import Link from '@material-ui/core/Link';
import Paper from '@material-ui/core/Paper';
import { SimpleFlexContainer } from '../layouts/ng-layouts/Containers';
import { formatFileSize } from '../form/engine/FileUploadFieldEngine';

export const Container = styled(SimpleFlexContainer)`
  overflow: auto;

  ${({ $customCSS }) => $customCSS}
  text-align: left;
  width: 90%;
  padding-top: 2rem;

  h1 {
    margin: 0.8rem 0;
  }
`;

const DropTarget = styled.div`
  position: relative;
  background: ${({ theme }) => theme.palette.pale_grey};
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 5px;
  color: #2b6c80;
  display: flex;
  align-items: center;
  flex-direction: column;
  font-size: 14px;
  text-align: center;
  overflow: hidden;
  padding: 12px;

  &:focus {
    outline: none;
  }

  a {
    cursor: pointer;
  }

  .dropzone-text {
    font-size: 14px;
  }

  [class*='MuiCollapse'] {
    width: 100%;
  }

  ${({ $isDragActive, theme }) =>
    $isDragActive &&
    'animation: pulse 2s ease-in-out infinite alternate; border: 2px dashed #87c2d5;'}

  transition: border 0.2s;

  @keyframes pulse {
    0% {
      background: #f7f8fc;
    }
    50% {
      background-color: #eaeeff;
    }
    100% {
      background-color: #f7f8fc;
    }
  }
`;

const CloudUpload = styled(CloudUploadIcon)`
  && {
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 64px;
    margin-top: 1rem;
  }
`;

const LinearProgress = styled(MuiLinearProgress)`
  position: absolute !important;
  bottom: 0;

  opacity: ${({ $isVisible }) => ($isVisible ? '1' : '0')};
`;

const DropZoneInnerContainer = styled(SimpleFlexContainer)`
  align-items: center;
  padding-bottom: 1rem;
  width: 100%;
`;

const FileCardPaper = styled(Paper)`
  && {
    width: 100%;
    margin: 12px 0 0;
    padding: 8px 12px;
    display: flex;
    align-items: center;

    .FCP__svg {
      opacity: 0.3;
      margin-right: 1ch;
    }

    .FCP__filename {
      font-weight: bold;
      flex: 1;
      text-align: left;

      /* https://css-tricks.com/snippets/css/prevent-long-urls-from-breaking-out-of-container/ */
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-all;
      word-break: break-word;

      a {
        font-weight: bold;
      }
    }

    .FCP__size {
      opacity: 0.5;
      font-weight: 400;
      padding: 0 0.5ch;
      word-break: keep-all;
      overflow-wrap: normal;
      word-wrap: normal;
    }

    .FCP__progress-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: none;
      width: 200px;
      max-width: 50vw;
      padding: 1ch;
    }
  }
`;

const FileCard = ({ file }) => {
  let { name, size, progress, complete } = file;

  const filename = name || '';

  return (
    <Animation type="zoom" appear>
      <FileCardPaper>
        <AttachmentIcon className="FCP__svg" />
        <span className="FCP__filename">
          {filename}
          <span className="FCP__size">&nbsp;({formatFileSize(size)})</span>
        </span>

        <Animation type="zoom" in={!complete}>
          <span className="FCP__progress-wrapper" data-testid={'file-progress'}>
            {progress < 99 ? (
              <LinearProgress
                style={{ width: '100%' }}
                variant="determinate"
                value={progress}
                data-testid={'file-progress-uploading'}
              />
            ) : (
              <LinearProgress
                style={{ width: '100%' }}
                data-testid={'file-progress-waiting'}
              />
            )}
          </span>
        </Animation>
      </FileCardPaper>
    </Animation>
  );
};

const FileUploadView = props => {
  const t = useTranslations();
  const { getRootProps, getInputProps, isDragActive } = props.dropzone;

  return (
    <Container $flex={1} className="container">
      <DropTarget
        {...getRootProps({ onClick: props.onDropZoneClicked })}
        $isDragActive={isDragActive}
        onClick={props.onDropZoneClicked}
      >
        <input
          {...getInputProps()}
          data-testid="dropzone-input"
          disabled={false}
        />
        <DropZoneInnerContainer>
          <CloudUpload />
          <div className="dropzone-text">
            {props.progress > 0 && props.progress < 100 ? (
              'Uploading...'
            ) : (
              <p className="dropzone-text">
                <Link className="dropzone-button" onClick={() => {}}>
                  {t('forms.field.fileupload.upload.first')}
                </Link>
                {' ' + t('forms.field.fileupload.upload.draghere')}
              </p>
            )}
          </div>
          <LinearProgress
            $isVisible={props.progress > 0}
            style={{ width: '100%' }}
            variant="determinate"
            value={props.progress}
            data-testid={'file-progress-uploading'}
          />
          {props.belowButtonBlock}
          {props.attachments.map(file => (
            <FileCard key={file.id} file={file} />
          ))}
        </DropZoneInnerContainer>
      </DropTarget>
    </Container>
  );
};

export default FileUploadView;
